// Nurse directory tab (Staff Roster redesign, 2026-07-27): search/filter
// table of all nurses. Extracted from StaffRoster.js's former inline "All
// Nurses" section. Owns its own search/duty filter state; receives the raw
// nurse list as a prop — no fetch of its own.
//
// Duty and today's shifts come from the roster for the roster date of the shift
// in progress, not from NurseRecord's on_duty flag and default hours, which
// nothing keeps current: every QA nurse read "ICU · 08:00–16:00 · Off" while the
// roster had two of them on duty (2026-09-24).
import React, { useCallback, useMemo, useState } from "react";
import {
  Avatar, Box, Chip, InputAdornment, LinearProgress, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { NURSE_COLORS, initials, nurseAvatarColor } from "../theme";
import { useDebouncedValue } from "../hooks";
import { shiftLabel } from "../shiftWindow";

const WORKING = new Set(["on_duty", "overtime"]);

export default function NurseDirectoryTab({ nurses, loading, todayRoster = [], shift, schedule, wardNameByCode = {}, bedCountByNurse = {} }) {
  const [search, setSearch] = useState("");
  const [duty, setDuty] = useState("All");
  const debouncedSearch = useDebouncedValue(search);

  // Each nurse's rows for today, in the order the day's shifts run.
  const rosterByNurse = useMemo(() => {
    const order = new Map((schedule || []).map((s) => [s.code, s.sort_order]));
    const map = {};
    todayRoster.forEach((r) => { (map[r.nurse_id] = map[r.nurse_id] || []).push(r); });
    Object.values(map).forEach((list) => list.sort((a, b) => (order.get(a.shift) ?? 99) - (order.get(b.shift) ?? 99)));
    return map;
  }, [todayRoster, schedule]);

  const dutyOf = useCallback((n) => {
    const list = rosterByNurse[n.nurse_id] || [];
    if (list.some((r) => r.shift === shift && WORKING.has(r.status))) return "On duty";
    if (list.some((r) => r.status === "leave")) return "On leave";
    return "Off";
  }, [rosterByNurse, shift]);

  const rows = useMemo(() => {
    const term = debouncedSearch.toLowerCase().trim();
    const onDuty = (n) => dutyOf(n) === "On duty";
    return nurses
      .filter((n) => (duty === "All" ? true : duty === "On duty" ? onDuty(n) : !onDuty(n)))
      .filter((n) => !term || (n.name || "").toLowerCase().includes(term) || (n.registration_no || "").toLowerCase().includes(term));
  }, [nurses, debouncedSearch, duty, dutyOf]);

  return (
    <Box>
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField
            size="small" placeholder="Search by name or ID" value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
            sx={{ flex: 1 }}
          />
          <TextField select size="small" label="Duty" value={duty} onChange={(e) => setDuty(e.target.value)} sx={{ minWidth: 160 }}>
            {["All", "On duty", "Off duty"].map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
          </TextField>
        </Stack>
      </Paper>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Paper sx={{ borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}`, overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
              <TableCell>Nurse</TableCell><TableCell>ID</TableCell><TableCell>Specialization</TableCell><TableCell>Today's roster</TableCell><TableCell>Duty</TableCell><TableCell align="right">Beds this shift</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((n) => {
              const status = dutyOf(n);
              const shifts = (rosterByNurse[n.nurse_id] || []).filter((r) => WORKING.has(r.status));
              return (
              <TableRow key={n.nurse_id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                    <Avatar sx={{ bgcolor: nurseAvatarColor(n.nurse_id), width: 32, height: 32, fontSize: 14 }}>{initials(n.name)}</Avatar>
                    <Typography variant="body2">{n.name}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>{n.registration_no || "—"}</TableCell>
                <TableCell>{n.specialization || "—"}</TableCell>
                <TableCell>
                  {shifts.length
                    ? shifts.map((r) => `${shiftLabel(r.shift, schedule)} · ${wardNameByCode[r.ward_code] || r.ward_code}${r.status === "overtime" ? " (overtime)" : ""}`).join(", ")
                    : "—"}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small" label={status}
                    color={status === "On duty" ? "success" : status === "On leave" ? "warning" : "default"}
                    variant={status === "On duty" ? "filled" : "outlined"}
                  />
                </TableCell>
                <TableCell align="right"><Chip size="small" color="primary" label={bedCountByNurse[n.nurse_id] || 0} /></TableCell>
              </TableRow>
              );
            })}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>No nurses match the filter.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
